import React, { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { TestBadge } from '../../tax/TaxBadges';
import { usePayApi, ModeBadge, TxStatus, money, when } from './shared';

const ActionDialog = ({ tx, kind, onClose, send }) => {
  const left = Number(((tx.paid_amount || tx.amount) - (tx.refunded_amount || 0)).toFixed(2));
  const [amount, setAmount] = useState(String(left));
  const [reason, setReason] = useState('');
  const go = async () => {
    const ok = kind === 'refund'
      ? await send('post', `/transactions/${tx.id}/refund`, { amount: parseFloat(amount), reason }, 'Refund issued')
      : await send('post', `/transactions/${tx.id}/cancel`, { reason }, 'Payment cancelled');
    if (ok) onClose();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid={`tx-${kind}-dialog`}>
        <DialogHeader><DialogTitle>{kind === 'refund' ? `Refund ${tx.customer_name || tx.customer_email}` : 'Cancel unpaid payment'}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          {kind === 'refund' && <><p>Up to {money(left)} can be refunded ({tx.mode} payment).</p><Input value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="tx-refund-amount" /></>}
          <Textarea rows={2} placeholder="Reason (required, kept in the audit history)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`tx-${kind}-reason`} />
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Close</Button><Button disabled={!reason.trim()} onClick={go} data-testid={`tx-${kind}-submit`}>{kind === 'refund' ? 'Issue refund' : 'Cancel payment'}</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const doc = (t) => t.invoice_number ? `Invoice ${t.invoice_number}` : t.quote_id ? `Deposit · quote #${t.quote_id.slice(0, 8).toUpperCase()}` : t.order_id ? `Order #${t.order_id.slice(0, 8).toUpperCase()}` : 'Website checkout';

export const PaymentsTransactions = ({ token, canEdit }) => {
  const [filters, setFilters] = useState({ status: '', purpose: '', mode: '', q: '' });
  const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
  const { data, send } = usePayApi(token, `/transactions${qs ? `?${qs}` : ''}`);
  const [dialog, setDialog] = useState(null);
  const sel = (k, opts) => (
    <select value={filters[k]} onChange={(e) => setFilters({ ...filters, [k]: e.target.value })} className="h-8 border rounded-md px-2 text-sm" data-testid={`tx-filter-${k}`}>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  return (
    <div className="bg-white border rounded-lg p-4" data-testid="payments-transactions">
      <div className="flex flex-wrap gap-2 mb-3">
        {sel('status', [['', 'All statuses'], ['paid', 'Paid'], ['pending', 'Pending'], ['partially_refunded', 'Partly refunded'], ['refunded', 'Refunded'], ['failed', 'Failed'], ['expired', 'Expired'], ['cancelled', 'Cancelled']])}
        {sel('purpose', [['', 'All types'], ['checkout', 'Website checkout'], ['invoice', 'Invoice'], ['deposit', 'Quote deposit']])}
        {sel('mode', [['', 'Test + live'], ['test', 'Test'], ['live', 'Live']])}
        <Input placeholder="Customer, invoice, transaction ID" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className="h-8 w-64" data-testid="tx-filter-q" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-neutral-500 border-b">{['Date', 'Customer', 'Quote / order / invoice', 'Provider', 'Amount', 'Status', 'Transaction ID', 'Payment method', 'Refunded', 'Mode', ''].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>{(data || []).map((t) => (
            <tr key={t.id} className="border-b align-top" data-testid={`tx-row-${t.id}`}>
              <td className="py-2 pr-3 whitespace-nowrap">{when(t.created_at)}</td>
              <td className="py-2 pr-3">{t.customer_name || '—'}<div className="text-neutral-400">{t.customer_email}</div><TestBadge doc={t} testId={`tx-test-badge-${t.id}`} /></td>
              <td className="py-2 pr-3">{doc(t)}{t.fulfillment_status === 'failed' && <div className="text-red-700" data-testid={`tx-fulfillment-failed-${t.id}`}>Paid, update failed: {t.fulfillment_error}</div>}</td>
              <td className="py-2 pr-3 capitalize">{t.provider}</td>
              <td className="py-2 pr-3 font-medium">{money(t.paid_amount || t.amount)}</td>
              <td className="py-2 pr-3"><TxStatus s={t.payment_status} testId={`tx-status-${t.id}`} /></td>
              <td className="py-2 pr-3 font-mono max-w-[11rem] break-all">{t.provider_payment_id || t.session_id}</td>
              <td className="py-2 pr-3">{t.method_summary || '—'}</td>
              <td className="py-2 pr-3">{t.refunded_amount ? `${money(t.refunded_amount)} (${t.refunds.length})` : '—'}</td>
              <td className="py-2 pr-3"><ModeBadge mode={t.mode} testId={`tx-mode-${t.id}`} /></td>
              <td className="py-2 whitespace-nowrap space-x-1">
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => send('post', `/transactions/${t.id}/sync`, {}, 'Checked with provider')} data-testid={`tx-sync-${t.id}`}>Re-check</Button>
                {canEdit && ['paid', 'partially_refunded'].includes(t.payment_status) && <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setDialog({ tx: t, kind: 'refund' })} data-testid={`tx-refund-${t.id}`}>Refund</Button>}
                {canEdit && t.payment_status === 'pending' && <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setDialog({ tx: t, kind: 'cancel' })} data-testid={`tx-cancel-${t.id}`}>Cancel</Button>}
                {['paid', 'partially_refunded', 'refunded'].includes(t.payment_status) && t.fulfillment_status === 'failed' && <Button size="sm" className="h-7 px-2" onClick={() => send('post', `/transactions/${t.id}/retry-fulfillment`, {}, 'Retried')} data-testid={`tx-retry-${t.id}`}>Retry update</Button>}
              </td>
            </tr>
          ))}</tbody>
        </table>
        {data && !data.length && <p className="text-sm text-neutral-500 p-4">No online payments match these filters.</p>}
      </div>
      {dialog && <ActionDialog {...dialog} onClose={() => setDialog(null)} send={send} />}
    </div>
  );
};
